import {
  LOG_OVERVIEW_PAGE_SIZE,
  type LogOverviewRow,
  type LogOverviewServerFilters,
  type LoggingOverviewDateRange,
  fetchLogOverview,
  fetchLogOverviewFilterValues,
} from '@/api/hooks/useIntegrationLogs'
import {
  clearRowCountCache,
  useServerSideDataSource,
} from '@/api/useServerSideDatasource/useServerSideDatasource'
import { ControlBarSearch } from '@/components/Display/ControlBarSearch'
import { EnvModeSwitch } from '@/components/Display/EnvModeSwitch'
import { useDebouncedSearch } from '@/pages/Dashboard/panels/ReferenceDataPanel/hooks/useDebouncedSearch'
import { useTenantStore } from '@/stores/tenantStore'
import { ALL_MODES, type EnvModeSelection, toModeParam } from '@/utils/envMode'
import { ClearOutlined, ReloadOutlined } from '@ant-design/icons'
import {
  GraviButton,
  GraviGrid,
  Horizontal,
  Vertical,
} from '@gravitate-js/excalibrr'
import type {
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GridApi,
} from 'ag-grid-community'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { LogDetailsSidebar } from '../ConnectionLogsPanel/components/LogDetailsSidebar'
import { useSupplyPlanLogColumnDefs } from '../ConnectionLogsPanel/components/LogsColumnDefs'
import { useLogDetailGrid } from '../ConnectionLogsPanel/components/useLogDetailGrid'
import { useLogSidebar } from '../ConnectionLogsPanel/components/useLogSidebar'
import { LogsRangePicker, defaultLogsRange } from '../LogsRangePicker'

export function SupplyPlanLogsPanel() {
  const selectedTenantName = useTenantStore().selectedTenant?.name

  // Read before the state below so a deep link can seed what it defaults to.
  const [searchParams, setSearchParams] = useSearchParams()
  const [batchFilter, setBatchFilter] = useState(() =>
    searchParams.get('batch_id'),
  )

  // A batch deep link overrides the window server-side, so the picker starts
  // empty rather than showing a range that is not being applied.
  const [dateRange, setDateRange] = useState<
    LoggingOverviewDateRange | undefined
  >(() => (searchParams.get('batch_id') ? undefined : defaultLogsRange()))

  // Arriving on a specific batch means the batch is the thing being looked at,
  // and it may well have synced through a Test instance -- defaulting to Prod
  // would show an empty grid rather than the batch the link points at.
  const [mode, setMode] = useState<EnvModeSelection>(() =>
    searchParams.get('batch_id') ? ALL_MODES : 'Prod',
  )

  const gridApiRef = useRef<GridApi | null>(null)
  const [hasActiveFilters, setHasActiveFilters] = useState(false)
  const { searchTerm, setSearchTerm } = useDebouncedSearch()

  const serverFilters = useMemo<LogOverviewServerFilters>(
    () => ({
      tenantName: selectedTenantName,
      type: 'supply_plan',
      dateRange,
      filter: null,
      mode: toModeParam(mode),
      batchId: batchFilter,
    }),
    [selectedTenantName, dateRange, mode, batchFilter],
  )

  const { serverSideDatasource, isFetching, rowCount } =
    useServerSideDataSource(
      selectedTenantName ? serverFilters : null,
      searchTerm,
      fetchLogOverview,
    )

  const getFilterOptions = useCallback(
    (column: string, filterModel: Record<string, unknown>) =>
      fetchLogOverviewFilterValues(
        column,
        serverFilters,
        filterModel,
        searchTerm,
      ),
    [serverFilters, searchTerm],
  )

  const refreshLogs = useCallback(() => {
    clearRowCountCache()
    gridApiRef.current?.refreshServerSide({ purge: true })
  }, [])

  const columnDefs = useSupplyPlanLogColumnDefs()

  const {
    selectedLog,
    selectedLogGroupId,
    selectedGroupMetadata,
    selectLog,
    onCellClicked,
    clearSelectedLog,
  } = useLogSidebar()
  const { getRowId, detailCellRendererParams, auditModalElement } =
    useLogDetailGrid({
      onLogSelected: selectLog,
      warningVariant: 'partial_success',
    })

  const onFirstDataRendered = useCallback(
    (event: FirstDataRenderedEvent<LogOverviewRow>) => {
      gridApiRef.current = event.api
    },
    [],
  )

  const agPropOverrides = useMemo(
    () => ({
      rowModelType: 'serverSide' as const,
      serverSideDatasource,
      pagination: true,
      paginationPageSize: LOG_OVERVIEW_PAGE_SIZE,
      cacheBlockSize: LOG_OVERVIEW_PAGE_SIZE,
      context: { getFilterOptions },
      domLayout: 'normal' as const,
      rowHeight: 45,
      getRowId,
      masterDetail: true,
      suppressRowClickSelection: true,
      detailCellRendererParams,
      detailRowHeight: 500,
      isRowMaster: () => true,
      onCellClicked,
      onFirstDataRendered,
      onFilterChanged: (event: FilterChangedEvent<LogOverviewRow>) =>
        setHasActiveFilters(event.api.isAnyFilterPresent()),
    }),
    [
      serverSideDatasource,
      getFilterOptions,
      getRowId,
      detailCellRendererParams,
      onCellClicked,
      onFirstDataRendered,
    ],
  )

  // The batch deep link is a server filter, the column filters are the grid's;
  // one button clears both, plus the deep link it arrived on.
  const clearFilters = useCallback(() => {
    setBatchFilter(null)
    setDateRange(defaultLogsRange())
    gridApiRef.current?.setFilterModel(null)
    setSearchParams(
      (params) => {
        params.delete('batch_id')
        return params
      },
      { replace: true },
    )
  }, [setSearchParams])

  return (
    <Horizontal gap={16} className='h-full' bg='elevated'>
      <Vertical flex={2.5}>
        <GraviGrid<LogOverviewRow, Record<string, unknown>>
          columnDefs={columnDefs}
          loading={isFetching}
          columnDefaultOverrides={{ resizable: true, enableRowGroup: false }}
          fullWidthSearchBar
          controlBarProps={{
            rowCount,
            title: 'Supply Option Integration Logs',
            customSearchBar: <ControlBarSearch onChange={setSearchTerm} />,
            actionButtons: (
              <Horizontal gap={20} alignItems='center'>
                {(batchFilter || hasActiveFilters) && (
                  <GraviButton
                    size='small'
                    appearance='outline'
                    color='blue'
                    icon={<ClearOutlined />}
                    onClick={clearFilters}
                  >
                    Clear Filters
                  </GraviButton>
                )}
                <EnvModeSwitch value={mode} onChange={setMode} />
                <LogsRangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  disabledReason={
                    batchFilter
                      ? 'Showing one batch -- the date range does not apply'
                      : undefined
                  }
                />
                <GraviButton
                  size='small'
                  appearance='outline'
                  color='blue'
                  icon={<ReloadOutlined />}
                  onClick={refreshLogs}
                >
                  Refresh
                </GraviButton>
              </Horizontal>
            ),
          }}
          agPropOverrides={agPropOverrides}
        />
      </Vertical>
      <LogDetailsSidebar
        selectedLog={selectedLog}
        groupMetadata={selectedGroupMetadata}
        supplyPlanBatchId={
          selectedGroupMetadata?.type === 'supply_plan_batch'
            ? selectedGroupMetadata.batch_id
            : null
        }
        selectedTenantName={selectedTenantName}
        logGroupId={selectedLogGroupId}
        refetchLogs={refreshLogs}
        onClose={clearSelectedLog}
      />
      {auditModalElement}
    </Horizontal>
  )
}
